import { kebabCase } from "lodash";
import { learnTagsSortedByPriority } from "./learnTagPriority";

/**
 * Interface for tag objects coming from Contentful
 */
export interface ITag {
  value: string;
  label: string;
  type?: string;
  hasDirectoryPage?: boolean;
  descriptionShort?: {
    descriptionShort: string;
  };
}

/**
 * Adds properly formatted metadata for the bankruptcy-by-state tag to match contentful tag format
 * @param tags Array of tags from Contentful
 * @returns A new array with properly formatted bankruptcy-by-state tag
 */
export const enrichWithBankruptcyByStateTag = (tags: ITag[]): ITag[] => {
  const bankruptcyByStateTag: ITag = {
    value: "bankruptcyByState",
    label: "Bankruptcy By State",
    type: "category",
    hasDirectoryPage: true,
    descriptionShort: {
      descriptionShort:
        "Learn how bankruptcy works in your state, from court locations to filing procedures and exemptions.",
    },
  };

  // Return tags plus our formatted tag (filtering out any existing duplicates to be safe)
  return [...tags.filter((tag) => tag.value !== "bankruptcyByState"), bankruptcyByStateTag];
};

// Pre-compute priority mapping for efficiency
const tagPriorityMap = (() => {
  const priorityValues = Object.values(learnTagsSortedByPriority);
  return Object.fromEntries(priorityValues.map((value, index) => [value, index]));
})();

/**
 * Enriches tags with bankruptcy-by-state and sorts them by priority
 * @param tags Array of tags from Contentful
 * @returns A new array with properly formatted tags sorted by priority
 */
export const getSortedTagsWithBankruptcyState = (tags: ITag[]): ITag[] => {
  const enrichedTags = enrichWithBankruptcyByStateTag(tags);

  return [...enrichedTags].sort((a, b) => {
    const priorityA = tagPriorityMap[a.value] ?? Number.MAX_SAFE_INTEGER;
    const priorityB = tagPriorityMap[b.value] ?? Number.MAX_SAFE_INTEGER;
    return priorityA - priorityB;
  });
};

/**
 * Gets the URL for a tag
 * @param tag The tag object
 * @returns The URL for the tag's page
 */
export const getTagUrl = (tag: ITag): string => {
  return tag.value === "bankruptcyByState" ? "/bankruptcy-by-state/" : `/learn/category/${kebabCase(tag.value)}/`;
};
